/**
 * Sun tracker config helpers for the simplified Season × Occupancy sun
 * controller (`shade_sun_tracking` automation). Mirrors the backend model in
 * `otto/src/services/automations/shadeSunTracker.ts`.
 */

import { tt } from './consoleText';
import type { Bilingual } from '../config/bilingual';
import type { DeviceAutomationConfig } from '../stores/deviceAutomations';
import { field, wireArray, wireNumber, wireObject } from './wire';

// ---------------------------------------------------------------------------
// Types (mirror backend — no shared package yet)
// ---------------------------------------------------------------------------

/**
 * A season id. The Shades category's seasons are custom — ids are generated —
 * and the seeded seasons keep the legacy `summer`/`winter` ids, so cell configs
 * written before custom seasons (and these defaults) keep resolving.
 */
export type SunSeasonKey = string;

export type SunOccupancyKey = 'presence' | 'absence' | 'sleep';

export type SunEventTriggerType = 'time' | 'sunrise' | 'sunset' | 'high_brightness' | 'low_brightness';

export interface SunWindowAnchor {
  type: 'time' | 'sunrise' | 'sunset';
  time?: string;
  offset_minutes?: number;
}

export interface SunEventTrigger {
  type: SunEventTriggerType;
  time?: string;
  offset_minutes?: number;
  threshold_lux?: number;
}

export interface SunEvent {
  trigger: SunEventTrigger;
  position: number;
  tilt: number;
}

export interface SunTrackParams {
  enabled: boolean;
  axis: 'azimuth' | 'altitude';
  slat_axis: 'off' | 'azimuth' | 'altitude';
  min_position: number;
  max_position: number;
  tilt_min: number;
  tilt_max: number;
  invert: boolean;
}

export interface SunCellConfig {
  position: number;
  tilt: number;
  events: SunEvent[];
  sun_tracking: SunTrackParams;
}

export interface SunOccupancyCells {
  presence: SunCellConfig;
  absence: SunCellConfig;
  sleep: SunCellConfig;
}

export interface SunCellsConfig {
  [seasonId: string]: SunOccupancyCells;
}

export interface ShadeSunWindowConfig {
  azimuth_min: number;
  azimuth_max: number;
  altitude_min: number;
}

export interface SunTrackerConfig {
  enabled: boolean;
  /** `'auto'` follows the Shades category's active season; an id pins it. */
  season_mode: 'auto' | string;
  /** @deprecated Climate transitional mapping — shade seasons are custom now. */
  transitional_season: 'summer' | 'winter';
  sleep_window: { start: SunWindowAnchor; end: SunWindowAnchor };
  brightness_hysteresis_lux: number;
  delay_on_seconds: number;
  delay_off_seconds: number;
  priority?: number;
  default_position: number | null;
  sun_window: ShadeSunWindowConfig;
  cells: SunCellsConfig;
}

export interface LiveSunInput {
  azimuth: number;
  altitude: number;
  brightness?: number;
  occupied?: boolean;
  isDay?: boolean;
}

export type SunPositionSource = 'sun_tracking' | 'brightness' | 'schedule' | 'none';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * One of the sun controller's own label entries (`SUN_*_LABELS` and
 * `SUN_ANCHOR_TYPES`, `Bilingual` pairs) in the reader's language. The tables
 * stay data — only the render picks a half — so the same entry serves the
 * editor, the panel and the validation sentences without knowing the locale.
 */
export function sunLabel(entry: Bilingual): string {
  return tt(entry.en, entry.sl);
}

export const SUN_SEASON_KEYS: SunSeasonKey[] = ['summer', 'winter'];

export const SUN_OCCUPANCY_KEYS: SunOccupancyKey[] = ['presence', 'absence', 'sleep'];

export const SUN_SEASON_LABELS: Record<string, Bilingual> = {
  summer: { en: 'Summer', sl: 'Poletje' },
  winter: { en: 'Winter', sl: 'Zima' },
};

export const SUN_OCCUPANCY_LABELS: Record<SunOccupancyKey, Bilingual> = {
  presence: { en: 'Presence', sl: 'Prisotnost' },
  absence: { en: 'Absence', sl: 'Odsotnost' },
  sleep: { en: 'Sleep', sl: 'Spanje' },
};

/** Drawn `ControlIcon` names (card t_a8249a54) — the sheet's own set, not Material. */
export const SUN_OCCUPANCY_ICONS: Record<SunOccupancyKey, string> = {
  presence: 'person',
  absence: 'person-off',
  sleep: 'moon',
};

export const SUN_EVENT_TRIGGER_LABELS: Record<SunEventTriggerType, Bilingual> = {
  time: { en: 'Time', sl: 'Čas' },
  sunrise: { en: 'Sunrise', sl: 'Sončni vzhod' },
  sunset: { en: 'Sunset', sl: 'Sončni zahod' },
  high_brightness: { en: 'High Brightness', sl: 'Visoka svetlost' },
  low_brightness: { en: 'Low Brightness', sl: 'Nizka svetlost' },
};

export const SUN_SOURCE_LABELS: Record<SunPositionSource, Bilingual> = {
  sun_tracking: { en: 'Sun tracking', sl: 'Sončno vodenje' },
  brightness: { en: 'Brightness', sl: 'Svetlost' },
  schedule: { en: 'Schedule', sl: 'Urnik' },
  none: { en: 'None', sl: 'Brez' },
};

export const SUN_WINDOW_DEFAULTS: ShadeSunWindowConfig = {
  azimuth_min: 0,
  azimuth_max: 360,
  altitude_min: 0,
};

/** Default sleep window: 22:00 → 07:00 (wraps overnight). */
export const SUN_SLEEP_WINDOW_DEFAULTS: {
  start: SunWindowAnchor;
  end: SunWindowAnchor;
} = {
  start: { type: 'time', time: '22:00' },
  end: { type: 'time', time: '07:00' },
};

// ---------------------------------------------------------------------------
// Defaults ("best settings")
// ---------------------------------------------------------------------------

export function getSunTrackDefaults(): SunTrackParams {
  return {
    enabled: false,
    axis: 'azimuth',
    slat_axis: 'altitude',
    min_position: 0,
    max_position: 100,
    tilt_min: 0,
    tilt_max: 70,
    invert: false,
  };
}

export function getSunCellDefaults(): SunCellConfig {
  return {
    position: 100,
    tilt: 0,
    events: [],
    sun_tracking: getSunTrackDefaults(),
  };
}

export function getSunCellsDefaults(): SunCellsConfig {
  return {
    summer: {
      presence: {
        position: 100,
        tilt: 0,
        events: [
          { trigger: { type: 'high_brightness', threshold_lux: 30000 }, position: 20, tilt: 0 },
          { trigger: { type: 'sunset' }, position: 0, tilt: 0 },
        ],
        sun_tracking: { ...getSunTrackDefaults(), enabled: true },
      },
      absence: {
        position: 20,
        tilt: 0,
        events: [
          { trigger: { type: 'high_brightness', threshold_lux: 30000 }, position: 20, tilt: 0 },
          { trigger: { type: 'sunset' }, position: 0, tilt: 0 },
        ],
        sun_tracking: getSunTrackDefaults(),
      },
      sleep: {
        position: 0,
        tilt: 0,
        events: [],
        sun_tracking: getSunTrackDefaults(),
      },
    },
    winter: {
      presence: {
        position: 100,
        tilt: 0,
        events: [{ trigger: { type: 'sunset' }, position: 0, tilt: 0 }],
        sun_tracking: { ...getSunTrackDefaults(), enabled: true },
      },
      absence: {
        position: 100,
        tilt: 0,
        events: [{ trigger: { type: 'low_brightness', threshold_lux: 5000 }, position: 0, tilt: 0 }],
        sun_tracking: getSunTrackDefaults(),
      },
      sleep: {
        position: 0,
        tilt: 0,
        events: [],
        sun_tracking: getSunTrackDefaults(),
      },
    },
  };
}

export function getSunTrackerDefaults(): SunTrackerConfig {
  return {
    enabled: true,
    season_mode: 'auto',
    transitional_season: 'summer',
    sleep_window: {
      start: { ...SUN_SLEEP_WINDOW_DEFAULTS.start },
      end: { ...SUN_SLEEP_WINDOW_DEFAULTS.end },
    },
    brightness_hysteresis_lux: 5000,
    delay_on_seconds: 60,
    delay_off_seconds: 300,
    priority: 50,
    default_position: null,
    sun_window: { ...SUN_WINDOW_DEFAULTS },
    cells: getSunCellsDefaults(),
  };
}

/**
 * Pick the occupancy cells for a season id: the exact key first, then a legacy
 * `summer`/`winter` match by name, else the Summer defaults (mirrors the
 * backend's `resolveCellForSeason`).
 */
export function resolveCellForSeason(cells: SunCellsConfig, seasonId: string): SunOccupancyCells {
  const exact = cells[seasonId];
  if (exact) return exact;

  const key = (seasonId ?? '').toLowerCase();
  if (key.includes('summer') && cells.summer) return cells.summer;
  if (key.includes('winter') && cells.winter) return cells.winter;

  return cells.summer ?? Object.values(cells)[0] ?? getSunCellsDefaults().summer;
}

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function num(value: unknown, fallback: number, min?: number, max?: number): number {
  const n =
    typeof value === 'number' && Number.isFinite(value)
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : fallback;
  const resolved = Number.isFinite(n) ? n : fallback;
  return min !== undefined && max !== undefined ? clamp(resolved, min, max) : resolved;
}

function normalizeAnchor(raw: unknown, fallback: SunWindowAnchor): SunWindowAnchor {
  const anchor = wireObject(raw);
  if (!anchor) return { ...fallback };
  return {
    type: anchor.type === 'sunrise' || anchor.type === 'sunset' || anchor.type === 'time' ? anchor.type : fallback.type,
    time: typeof anchor.time === 'string' ? anchor.time : fallback.time,
    offset_minutes: typeof anchor.offset_minutes === 'number' ? anchor.offset_minutes : fallback.offset_minutes,
  };
}

function normalizeTrackParams(raw: unknown, fallback: SunTrackParams): SunTrackParams {
  const params = wireObject(raw);
  if (!params) return { ...fallback };
  return {
    enabled: typeof params.enabled === 'boolean' ? params.enabled : fallback.enabled,
    axis: params.axis === 'altitude' ? 'altitude' : 'azimuth',
    slat_axis: params.slat_axis === 'azimuth' || params.slat_axis === 'altitude' ? params.slat_axis : 'off',
    min_position: num(params.min_position, fallback.min_position, 0, 100),
    max_position: num(params.max_position, fallback.max_position, 0, 100),
    tilt_min: num(params.tilt_min, fallback.tilt_min, 0, 100),
    tilt_max: num(params.tilt_max, fallback.tilt_max, 0, 100),
    invert: typeof params.invert === 'boolean' ? params.invert : fallback.invert,
  };
}

function normalizeEvent(raw: unknown): SunEvent {
  const event = wireObject(raw) ?? {};
  const trigger = wireObject(event.trigger) ?? {};
  const type: SunEventTriggerType =
    trigger.type === 'sunrise' ||
    trigger.type === 'sunset' ||
    trigger.type === 'high_brightness' ||
    trigger.type === 'low_brightness' ||
    trigger.type === 'time'
      ? trigger.type
      : 'time';
  return {
    trigger: {
      type,
      time: typeof trigger.time === 'string' ? trigger.time : undefined,
      offset_minutes: typeof trigger.offset_minutes === 'number' ? trigger.offset_minutes : undefined,
      threshold_lux: typeof trigger.threshold_lux === 'number' ? trigger.threshold_lux : undefined,
    },
    position: num(event.position, 100, 0, 100),
    tilt: num(event.tilt, 0, 0, 100),
  };
}

function normalizeCell(raw: unknown, fallback: SunCellConfig): SunCellConfig {
  const cell = wireObject(raw);
  if (!cell) {
    return {
      position: fallback.position,
      tilt: fallback.tilt,
      events: fallback.events.map(normalizeEvent),
      sun_tracking: { ...fallback.sun_tracking },
    };
  }
  const events = wireArray(cell.events);
  return {
    position: num(cell.position, fallback.position, 0, 100),
    tilt: num(cell.tilt, fallback.tilt, 0, 100),
    events: events ? events.map(normalizeEvent) : fallback.events.map(normalizeEvent),
    sun_tracking: normalizeTrackParams(cell.sun_tracking, fallback.sun_tracking),
  };
}

/**
 * The keys only a pre-`cells` config carries. Their presence is what marks a
 * config as legacy and hands it to `migrateLegacyModes` — a fresh config (the
 * add modal's schema defaults) holds none of them, so the default cells stay
 * exactly as `getSunCellsDefaults()` built them.
 */
const LEGACY_SUN_CONFIG_KEYS = [
  'sun_tracking_mode',
  'modes',
  'sun_active_position',
  'sun_inactive_position',
  'sun_active_tilt',
  'sun_inactive_tilt',
  'facade_azimuth_min',
  'facade_azimuth_max',
  'facade_altitude_min',
  'presence_timeout_minutes',
  'presence_required',
  'brightness_threshold_lux',
] as const;

function isLegacySunConfig(config: Record<string, unknown>): boolean {
  return LEGACY_SUN_CONFIG_KEYS.some((key) => config[key] !== undefined);
}

/**
 * Migrate a legacy 5-mode config onto the simplified cells model.
 *
 * The legacy payload is untyped JSON: each mode bag is read with a shape check
 * rather than a widened read, and the two always-truthy fallbacks restore what
 * the old code spelled as `legacyModes?.x?.enabled ?? …`.
 */
function migrateLegacyModes(cells: SunCellsConfig, config: Record<string, unknown>): void {
  const legacy = config.sun_tracking_mode;
  const legacyModes = field(config, 'modes');
  const heatProtection = field(legacyModes, 'heat_protection');
  const passiveHeating = field(legacyModes, 'passive_heating');
  const lightTracking = field(legacyModes, 'light_tracking');

  const heatEnabled = field(heatProtection, 'enabled') ?? legacy !== 'passive_heating';
  const passiveEnabled = field(passiveHeating, 'enabled') ?? legacy === 'passive_heating';
  const slatAxis = field(lightTracking, 'slat_axis');

  const highThreshold = wireNumber(config.brightness_threshold_lux) ?? 30000;
  const activePos = wireNumber(config.sun_active_position) ?? 80;
  const inactivePos = wireNumber(config.sun_inactive_position) ?? 0;

  if (heatEnabled) {
    const pos = wireNumber(field(heatProtection, 'position')) ?? activePos;
    for (const season of SUN_SEASON_KEYS) {
      cells[season].absence.position = pos;
      cells[season].absence.events.push({
        trigger: { type: 'high_brightness', threshold_lux: highThreshold },
        position: pos,
        tilt: 0,
      });
    }
  }

  if (passiveEnabled) {
    const pos = wireNumber(field(passiveHeating, 'position')) ?? inactivePos;
    cells.winter.absence.position = pos;
    cells.winter.presence.position = 100;
  }

  if (field(lightTracking, 'enabled')) {
    const params: SunTrackParams = {
      enabled: true,
      axis: field(lightTracking, 'axis') === 'altitude' ? 'altitude' : 'azimuth',
      slat_axis: slatAxis === 'azimuth' || slatAxis === 'altitude' ? slatAxis : 'altitude',
      min_position: num(field(lightTracking, 'min_position'), 0, 0, 100),
      max_position: num(field(lightTracking, 'max_position'), 100, 0, 100),
      tilt_min: num(field(lightTracking, 'tilt_min'), 0, 0, 100),
      tilt_max: num(field(lightTracking, 'tilt_max'), 70, 0, 100),
      invert: Boolean(field(lightTracking, 'invert')),
    };
    for (const season of SUN_SEASON_KEYS) {
      cells[season].presence.sun_tracking = params;
    }
  }
}

/**
 * Normalize a raw config (possibly legacy) into the simplified Season ×
 * Occupancy structure with validated defaults. Safe to call on
 * partial/empty configs.
 */
export function normalizeSunTrackerConfig(raw?: unknown): SunTrackerConfig {
  const config = wireObject(raw) ?? {};
  const defaults = getSunTrackerDefaults();
  const sleepWindow = field(config, 'sleep_window');
  const sunWindow = field(config, 'sun_window');
  const rawCells = field(config, 'cells');

  const normalized: SunTrackerConfig = {
    enabled: typeof config.enabled === 'boolean' ? config.enabled : defaults.enabled,
    season_mode:
      typeof config.season_mode === 'string' && config.season_mode.trim() !== ''
        ? config.season_mode.trim()
        : defaults.season_mode,
    transitional_season: config.transitional_season === 'winter' ? 'winter' : defaults.transitional_season,
    sleep_window: {
      start: normalizeAnchor(field(sleepWindow, 'start'), defaults.sleep_window.start),
      end: normalizeAnchor(field(sleepWindow, 'end'), defaults.sleep_window.end),
    },
    brightness_hysteresis_lux: num(config.brightness_hysteresis_lux, defaults.brightness_hysteresis_lux, 0, 50000),
    delay_on_seconds: num(config.delay_on_seconds, defaults.delay_on_seconds, 0, 3600),
    delay_off_seconds: num(config.delay_off_seconds, defaults.delay_off_seconds, 0, 7200),
    priority: num(config.priority, defaults.priority ?? 50, 1, 100),
    default_position: config.default_position == null ? null : clamp(Number(config.default_position) || 0, 0, 100),
    sun_window: {
      azimuth_min: num(
        field(sunWindow, 'azimuth_min') ?? config.facade_azimuth_min,
        defaults.sun_window.azimuth_min,
        0,
        360,
      ),
      azimuth_max: num(
        field(sunWindow, 'azimuth_max') ?? config.facade_azimuth_max,
        defaults.sun_window.azimuth_max,
        0,
        360,
      ),
      altitude_min: num(
        field(sunWindow, 'altitude_min') ?? config.facade_altitude_min,
        defaults.sun_window.altitude_min,
        0,
        90,
      ),
    },
    cells: getSunCellsDefaults(),
  };

  // Explicit cells config wins. Keys are shade season ids; a custom id without
  // defaults yet falls back to the Summer cells (mirrors the backend).
  if (rawCells && typeof rawCells === 'object') {
    for (const [seasonId, rawSeason] of Object.entries(rawCells as Record<string, unknown>)) {
      if (!rawSeason || typeof rawSeason !== 'object') continue;
      const fallbackCells = defaults.cells[seasonId] ?? defaults.cells.summer;
      const seasonCells: SunOccupancyCells = {
        presence: fallbackCells.presence,
        absence: fallbackCells.absence,
        sleep: fallbackCells.sleep,
      };
      for (const occupancy of SUN_OCCUPANCY_KEYS) {
        seasonCells[occupancy] = normalizeCell(field(rawSeason, occupancy), fallbackCells[occupancy]);
      }
      normalized.cells[seasonId] = seasonCells;
    }
    return normalized;
  }

  // Legacy migration — only for a config that actually carries legacy fields.
  // Running it on a fresh config used to push the legacy heat-protection
  // brightness event on top of the default cells, duplicating Summer ·
  // Absence's own high-brightness event; the phantom duplicate then failed
  // `getSunTrackerErrors`, which keeps the add modal's Create button disabled.
  if (!isLegacySunConfig(config)) return normalized;

  migrateLegacyModes(normalized.cells, config);
  if (typeof config.brightness_threshold_lux === 'number') {
    for (const season of SUN_SEASON_KEYS) {
      for (const cell of Object.values(normalized.cells[season])) {
        for (const event of cell.events) {
          if (event.trigger.type === 'high_brightness') {
            event.trigger.threshold_lux = config.brightness_threshold_lux;
          }
        }
      }
    }
  }

  return normalized;
}

// ---------------------------------------------------------------------------
// Live evaluation (UI preview — mirrors backend computeState)
// ---------------------------------------------------------------------------

/** True when the sun azimuth/altitude is inside the configured window. */
export function sunInWindow(sunWindow: ShadeSunWindowConfig, sun: LiveSunInput | null): boolean {
  if (!sun) return false;
  const { azimuth_min: azMin, azimuth_max: azMax, altitude_min: altMin } = sunWindow;
  const inAz =
    azMin <= azMax ? sun.azimuth >= azMin && sun.azimuth <= azMax : sun.azimuth >= azMin || sun.azimuth <= azMax;
  return inAz && sun.altitude >= altMin;
}

function mapRange(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  if (inMax === inMin) return outMin;
  const t = clamp((value - inMin) / (inMax - inMin), 0, 1);
  return outMin + t * (outMax - outMin);
}

function peakRatio(value: number, min: number, max: number): number {
  if (max === min) return 1;
  const center = (min + max) / 2;
  const half = Math.max((max - min) / 2, 0.0001);
  return clamp(1 - Math.abs(value - center) / half, 0, 1);
}

function anchorMinutes(anchor: SunWindowAnchor | undefined): number | null {
  if (!anchor || anchor.type !== 'time' || !anchor.time) return null;
  return timeToMinutes(anchor.time);
}

/** Resolve a sleep-window anchor to minutes of day (only time anchors). */
function isInSleepWindowMinutes(window: { start: SunWindowAnchor; end: SunWindowAnchor }, now: Date): boolean {
  const start = anchorMinutes(window.start);
  const end = anchorMinutes(window.end);
  if (start == null || end == null) return false;
  const current = now.getHours() * 60 + now.getMinutes();
  if (start <= end) return current >= start && current <= end;
  return current >= start || current <= end;
}

/**
 * Compute which cell/source would be active given live inputs. Simplified
 * version of the backend decision (no debounce; brightness hysteresis
 * applied from the `wasBrightnessActive` flag). Used for live preview.
 */
export function computeActiveCell(
  config: SunTrackerConfig,
  season: SunSeasonKey | null,
  sun: LiveSunInput | null,
  wasBrightnessActive = false,
): {
  season: SunSeasonKey | null;
  occupancy: SunOccupancyKey | null;
  source: SunPositionSource;
  position: number | null;
  tilt: number | null;
  sunInWindow: boolean;
} {
  if (!sun || !season) {
    return { season, occupancy: null, source: 'none', position: null, tilt: null, sunInWindow: false };
  }
  const inWindow = sunInWindow(config.sun_window, sun);

  const occupied = sun.occupied ?? true;
  const occupancy: SunOccupancyKey = occupied
    ? isInSleepWindowMinutes(config.sleep_window, new Date())
      ? 'sleep'
      : 'presence'
    : 'absence';

  const cell = resolveCellForSeason(config.cells, season)[occupancy];
  const brightness = sun.brightness ?? 0;

  // 1) Sun tracking
  if (cell.sun_tracking.enabled && inWindow) {
    const params = cell.sun_tracking;
    const { azimuth, altitude } = sun;
    let position: number;
    if (params.axis === 'altitude') {
      let ratio = mapRange(altitude, config.sun_window.altitude_min, 90, 0, 1);
      if (params.invert) ratio = 1 - ratio;
      position = params.min_position + ratio * (params.max_position - params.min_position);
    } else {
      const ratio = peakRatio(azimuth, config.sun_window.azimuth_min, config.sun_window.azimuth_max);
      const mapped = params.min_position + ratio * (params.max_position - params.min_position);
      position = params.invert ? params.min_position + params.max_position - mapped : mapped;
    }
    let tilt = 0;
    if (params.slat_axis === 'altitude') {
      tilt = mapRange(altitude, config.sun_window.altitude_min, 90, params.tilt_min, params.tilt_max);
    } else if (params.slat_axis === 'azimuth') {
      const ratio = peakRatio(azimuth, config.sun_window.azimuth_min, config.sun_window.azimuth_max);
      tilt = params.tilt_min + ratio * (params.tilt_max - params.tilt_min);
    }
    return {
      season,
      occupancy,
      source: 'sun_tracking',
      position: Math.round(clamp(position, 0, 100)),
      tilt: Math.round(clamp(tilt, 0, 100)),
      sunInWindow: inWindow,
    };
  }

  // 2) Brightness event
  if (sun.brightness != null) {
    const hyst = wasBrightnessActive ? config.brightness_hysteresis_lux : 0;
    for (const event of cell.events) {
      const t = event.trigger;
      if (t.type === 'high_brightness' && brightness >= (t.threshold_lux ?? 30000) - hyst) {
        return {
          season,
          occupancy,
          source: 'brightness',
          position: event.position,
          tilt: event.tilt,
          sunInWindow: inWindow,
        };
      }
      if (t.type === 'low_brightness' && brightness <= (t.threshold_lux ?? 5000) + hyst) {
        return {
          season,
          occupancy,
          source: 'brightness',
          position: event.position,
          tilt: event.tilt,
          sunInWindow: inWindow,
        };
      }
    }
  }

  // 3) Schedule (most recent passed time/sunrise/sunset event)
  const scheduleEvents = cell.events.filter(
    (e) => e.trigger.type === 'time' || e.trigger.type === 'sunrise' || e.trigger.type === 'sunset',
  );
  if (scheduleEvents.length > 0) {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    let latest: SunEvent | null = null;
    let latestMinutes = -Infinity;
    for (const event of scheduleEvents) {
      const minutes = event.trigger.type === 'time' && event.trigger.time ? timeToMinutes(event.trigger.time) : null;
      if (minutes == null) continue; // solar anchors can't be previewed here
      const today = minutes;
      const yesterday = minutes - 24 * 60;
      if (today <= currentMinutes && today >= latestMinutes) {
        latest = event;
        latestMinutes = today;
      }
      if (yesterday <= currentMinutes && yesterday >= latestMinutes) {
        latest = event;
        latestMinutes = yesterday;
      }
    }
    return {
      season,
      occupancy,
      source: 'schedule',
      position: latest ? latest.position : cell.position,
      tilt: latest ? latest.tilt : cell.tilt,
      sunInWindow: inWindow,
    };
  }

  // 4) Nothing active
  return { season, occupancy, source: 'none', position: null, tilt: null, sunInWindow: inWindow };
}

export interface SunTrackerDisplay {
  automationId: string;
  name: string;
  icon: string;
  enabled: boolean;
  activeCell: string | null;
  activeSource: string | null;
  sunActive: boolean;
  sunInWindow: boolean;
  config: SunTrackerConfig;
}

function isTruthy(value: unknown): boolean {
  return value === true || value === 1 || value === 'true';
}

/** Parse a "<seasonId>:<occupancy>" cell key. */
export function parseCellKey(key: string | null | undefined): {
  season: SunSeasonKey | null;
  occupancy: SunOccupancyKey | null;
} {
  if (!key || typeof key !== 'string') return { season: null, occupancy: null };
  const [season, occupancy] = key.split(':');
  return {
    season: season ? season : null,
    occupancy: occupancy === 'presence' || occupancy === 'absence' || occupancy === 'sleep' ? occupancy : null,
  };
}

/**
 * The season's display name — the Shades season name when the caller provides
 * one, the legacy label for seeded ids, else the raw id.
 */
function seasonDisplayName(seasonId: string, seasonLabels?: Record<string, string>): string {
  const provided = seasonLabels?.[seasonId];
  if (provided) return provided;
  const legacy = SUN_SEASON_LABELS[seasonId];
  return legacy ? sunLabel(legacy) : seasonId;
}

export function cellLabel(key: string | null | undefined, seasonLabels?: Record<string, string>): string {
  const { season, occupancy } = parseCellKey(key);
  if (!season || !occupancy) return '—';
  return `${seasonDisplayName(season, seasonLabels)} · ${sunLabel(SUN_OCCUPANCY_LABELS[occupancy])}`;
}

/**
 * Build a compact display model for a `shade_sun_tracking` automation using
 * the live device values (active_cell / active_source / sun_active /
 * sun_in_window) plus the normalized config.
 */
export function buildSunTrackerDisplay(
  automation: {
    id: string;
    name: string;
    icon?: string;
    enabled: boolean;
    config: DeviceAutomationConfig;
  },
  currentValues: Record<string, unknown>,
): SunTrackerDisplay {
  const activeCellRaw = currentValues['shade_sun_tracking__active_cell'];
  const activeSourceRaw = currentValues['shade_sun_tracking__active_source'];
  let activeCell: string | null = null;
  if (typeof activeCellRaw === 'string' && activeCellRaw.includes(':')) {
    activeCell = activeCellRaw;
  }
  let activeSource: string | null = null;
  if (
    typeof activeSourceRaw === 'string' &&
    (activeSourceRaw === 'sun_tracking' ||
      activeSourceRaw === 'brightness' ||
      activeSourceRaw === 'schedule' ||
      activeSourceRaw === 'none' ||
      activeSourceRaw === 'inhibited' ||
      activeSourceRaw === 'disabled')
  ) {
    activeSource = activeSourceRaw;
  }

  return {
    automationId: automation.id,
    name: automation.name,
    icon: automation.icon || 'wb_sunny',
    enabled: automation.enabled,
    activeCell,
    activeSource,
    sunActive: isTruthy(currentValues['shade_sun_tracking__sun_active']),
    sunInWindow: isTruthy(currentValues['shade_sun_tracking__sun_in_window']),
    config: normalizeSunTrackerConfig(automation.config),
  };
}

// ---------------------------------------------------------------------------
// Schedule helpers
// ---------------------------------------------------------------------------

export const SUN_ANCHOR_TYPES = [
  { value: 'time', label: { en: 'Clock time', sl: 'Ura' } },
  { value: 'sunrise', label: { en: 'Sunrise', sl: 'Sončni vzhod' } },
  { value: 'sunset', label: { en: 'Sunset', sl: 'Sončni zahod' } },
] as const;

export function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

export function minutesToTime(minutes: number): string {
  const clamped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function anchorLabel(anchor: SunWindowAnchor | undefined): string {
  if (!anchor) return '—';
  if (anchor.type === 'time') return anchor.time || '—';
  const base = anchor.type === 'sunrise' ? 'Sunrise' : 'Sunset';
  const offset = anchor.offset_minutes ?? 0;
  if (offset === 0) return base;
  return `${base} ${offset > 0 ? '+' : ''}${offset}min`;
}

export function defaultAnchor(type: 'time' | 'sunrise' | 'sunset'): SunWindowAnchor {
  if (type === 'sunrise') return { type: 'sunrise', offset_minutes: 0 };
  if (type === 'sunset') return { type: 'sunset', offset_minutes: 0 };
  return { type: 'time', time: '08:00' };
}

/**
 * Validation errors — currently checks for duplicate time events within each
 * cell (same trigger time) and duplicate brightness thresholds. Every season
 * cell is checked; `seasonLabels` names custom seasons in the sentences.
 */
export function getSunTrackerErrors(config: unknown, seasonLabels?: Record<string, string>): string[] {
  const normalized = normalizeSunTrackerConfig(config);
  const errors = new Set<string>();

  for (const season of Object.keys(normalized.cells)) {
    for (const occupancy of SUN_OCCUPANCY_KEYS) {
      const cell = normalized.cells[season][occupancy];
      const label = `${seasonDisplayName(season, seasonLabels)} · ${sunLabel(SUN_OCCUPANCY_LABELS[occupancy])}`;
      const timeKeys = new Set<string>();
      const brightnessKeys = new Set<string>();
      for (const event of cell.events) {
        const t = event.trigger;
        if (t.type === 'time') {
          const key = t.time ?? '';
          if (timeKeys.has(key))
            errors.add(tt(`Duplicate time event (${key}) in ${label}`, `Podvojen časovni dogodek (${key}) v ${label}`));
          timeKeys.add(key);
        } else if (t.type === 'sunrise' || t.type === 'sunset') {
          const key = `${t.type}:${t.offset_minutes ?? 0}`;
          if (timeKeys.has(key))
            errors.add(
              tt(
                `Duplicate ${t.type} event in ${label}`,
                `Podvojen dogodek ${t.type === 'sunrise' ? 'sončnega vzhoda' : 'sončnega zahoda'} v ${label}`,
              ),
            );
          timeKeys.add(key);
        } else {
          const key = `${t.type}:${t.threshold_lux ?? ''}`;
          if (brightnessKeys.has(key))
            errors.add(
              tt(
                `Duplicate ${t.type === 'high_brightness' ? 'high' : 'low'} brightness event (${t.threshold_lux ?? ''} lux) in ${label}`,
                `Podvojen dogodek ${t.type === 'high_brightness' ? 'visoke' : 'nizke'} svetlosti (${t.threshold_lux ?? ''} lux) v ${label}`,
              ),
            );
          brightnessKeys.add(key);
        }
      }
    }
  }

  return [...errors];
}
